import { InboxOutlined } from "@ant-design/icons";
import { Alert, Input, Modal, Radio, Table, Upload } from "antd";
import { useEffect, useState } from "react";
import { useImportProperties } from "../api/queries.js";
import type { ImportResult } from "../api/types.js";

interface ImportEntry {
  name: string;
  target: string;
  value: string;
  description?: string;
}

function parseImportFile(text: string): ImportEntry[] {
  const raw: unknown = JSON.parse(text);
  const list = Array.isArray(raw) ? raw : (raw as { entries?: unknown }).entries;
  if (!Array.isArray(list)) {
    throw new Error("expected an array or an export file with an entries[] field");
  }
  return list.map((item, index) => {
    const entry = item as Partial<ImportEntry>;
    if (
      typeof entry.name !== "string" ||
      typeof entry.target !== "string" ||
      typeof entry.value !== "string"
    ) {
      throw new Error(`entry #${index} must have string name, target and value`);
    }
    return {
      name: entry.name,
      target: entry.target,
      value: entry.value,
      description: typeof entry.description === "string" ? entry.description : undefined,
    };
  });
}

interface ImportModalProps {
  application: string;
  open: boolean;
  onClose: () => void;
}

export function ImportModal({ application, open, onClose }: ImportModalProps) {
  const [entries, setEntries] = useState<ImportEntry[] | null>(null);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [mode, setMode] = useState<"create" | "overwrite">("create");
  const [note, setNote] = useState("");
  const [result, setResult] = useState<ImportResult | null>(null);
  const importMutation = useImportProperties();

  const reset = () => {
    setEntries(null);
    setFileName("");
    setParseError(null);
    setResult(null);
    setNote("");
    setMode("create");
  };

  // Start clean on every open — covers cancelled imports whose mutation
  // result lands after the modal was closed.
  useEffect(() => {
    if (open) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = () => {
    if (!entries) {
      return;
    }
    importMutation.mutate(
      { application, mode, message: note || undefined, entries },
      {
        onSuccess: setResult,
        onError: (error) => setParseError(error.message),
      },
    );
  };

  return (
    <Modal
      title={`Import properties into ${application}`}
      open={open}
      onCancel={() => {
        reset();
        onClose();
      }}
      onOk={result ? () => { reset(); onClose(); } : submit}
      okText={result ? "Close" : `Import${entries ? ` ${entries.length}` : ""}`}
      okButtonProps={{ disabled: !result && !entries }}
      confirmLoading={importMutation.isPending}
      width={640}
    >
      {!result && (
        <>
          <Upload.Dragger
            accept=".json,application/json"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              file
                .text()
                .then((text) => {
                  setEntries(parseImportFile(text));
                  setFileName(file.name);
                  setParseError(null);
                })
                .catch((error: unknown) => {
                  // A failed parse must clear any previously loaded file —
                  // importing file A while the error names file B is a trap.
                  setEntries(null);
                  setFileName("");
                  setParseError(error instanceof Error ? error.message : String(error));
                });
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p>drop an export file here, or click to choose</p>
            <p className="dim" style={{ fontSize: 11 }}>
              accepts the dashboard's export format or a bare [{"{name, target, value}"}] array
            </p>
          </Upload.Dragger>
          {fileName && entries && (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message={`${fileName}: ${entries.length} entries ready`}
            />
          )}
          {parseError && (
            <Alert style={{ marginTop: 12 }} type="error" showIcon message={parseError} />
          )}
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <Radio.Group
              value={mode}
              onChange={(event) => setMode(event.target.value as "create" | "overwrite")}
              options={[
                { value: "create", label: "create only — skip entries that already exist" },
                { value: "overwrite", label: "overwrite — force-write every entry" },
              ]}
            />
            <Input
              placeholder="change note (audit)"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={1000}
            />
          </div>
        </>
      )}
      {result && (
        <>
          <Alert
            type={result.failed.length > 0 ? "warning" : "success"}
            showIcon
            message={`created ${result.created}, updated ${result.updated}, skipped ${result.skipped}, failed ${result.failed.length}`}
          />
          {result.failed.length > 0 && (
            <Table
              style={{ marginTop: 12 }}
              size="small"
              rowKey={(_row, index) => String(index)}
              pagination={false}
              dataSource={result.failed}
              columns={[
                { title: "name", dataIndex: "name", className: "mono" },
                { title: "target", dataIndex: "target", className: "mono" },
                { title: "error", dataIndex: "error" },
              ]}
            />
          )}
        </>
      )}
    </Modal>
  );
}
